import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useContext, useCallback, useMemo } from 'react';
import FocusTrap from 'focus-trap-react';
import { StencilContext } from "../../../context";
import { EventKeys, getUniqueId } from "../../../utils";
import { CALENDAR_HEIGHT, CALENDAR_WRAPPER_MAX_WIDTH, datePickerCalendarDefaultProps } from "../constants";
import YearButton from "./YearButton";
import { YEARS_IN_ROW, YEAR_PICKER_DEFAULT_PROPS } from "./constants";
import { YearRow } from "./helperComponents";
import { jsx as ___EmotionJSX } from "@emotion/core";

var getYearInAvailableBounds = function getYearInAvailableBounds(currentYear, yearRange) {
  var yearInBounds = currentYear;

  if (currentYear > yearRange.max) {
    yearInBounds = yearRange.max;
  }

  if (currentYear < yearRange.min) {
    yearInBounds = yearRange.min;
  }

  return yearInBounds;
};

var YearPicker = function YearPicker(_ref) {
  var currentYear = _ref.currentYear,
      goToMonth = _ref.goToMonth,
      hideYearPicker = _ref.hideYearPicker,
      _ref$maxYear = _ref.maxYear,
      maxYear = _ref$maxYear === void 0 ? YEAR_PICKER_DEFAULT_PROPS.maxYear : _ref$maxYear,
      _ref$minYear = _ref.minYear,
      minYear = _ref$minYear === void 0 ? YEAR_PICKER_DEFAULT_PROPS.minYear : _ref$minYear,
      month = _ref.month,
      triggerButtonRef = _ref.triggerButtonRef,
      _ref$yearPickerButton = _ref.yearPickerButtonAltText,
      yearPickerButtonAltText = _ref$yearPickerButton === void 0 ? datePickerCalendarDefaultProps.yearPickerButtonAltText : _ref$yearPickerButton;
  var yearRange = {
    min: minYear,
    max: maxYear
  };
  var boundedCurrentYear = getYearInAvailableBounds(currentYear, yearRange);

  var _useContext = useContext(StencilContext),
      isRTL = _useContext.isRTL;

  var _useState = useState(boundedCurrentYear),
      _useState2 = _slicedToArray(_useState, 2),
      focusedYear = _useState2[0],
      setFocusedYear = _useState2[1];

  var allYears = Array.from({
    length: maxYear - minYear + 1
  }, function (_, y) {
    return minYear + y;
  });
  var handleYearClick = useCallback(function (year) {
    goToMonth(month, year);
    hideYearPicker();
  }, [month, goToMonth, hideYearPicker]);
  var handleKeyDown = useCallback(function (event) {
    switch (event.key) {
      case EventKeys.ARROW_UP:
        setFocusedYear(function (year) {
          return year - YEARS_IN_ROW;
        });
        event.preventDefault();
        break;

      case EventKeys.ARROW_DOWN:
        setFocusedYear(function (year) {
          return year + YEARS_IN_ROW;
        });
        event.preventDefault();
        break;

      case EventKeys.ARROW_LEFT:
        setFocusedYear(function (year) {
          return year + (isRTL ? 1 : -1);
        });
        event.preventDefault();
        break;

      case EventKeys.ARROW_RIGHT:
        setFocusedYear(function (year) {
          return year + (isRTL ? -1 : 1);
        });
        event.preventDefault();
        break;

      default:
        break;
    }
  }, [setFocusedYear, isRTL]);
  var dialogHiddenTextId = useMemo(function () {
    return "dialog-hidden-text-".concat(getUniqueId(), "-id");
  }, []);
  return ___EmotionJSX(FocusTrap, {
    focusTrapOptions: {
      allowOutsideClick: true,
      clickOutsideDeactivates: function clickOutsideDeactivates(e) {
        return !triggerButtonRef || !triggerButtonRef.current || !e.composedPath().includes(triggerButtonRef.current);
      },
      escapeDeactivates: true,
      onDeactivate: hideYearPicker
    }
  }, ___EmotionJSX(YearRow, {
    flexWrap: "wrap",
    gridGap: "S100",
    justifyContent: "flex-start",
    maxHeight: CALENDAR_HEIGHT,
    maxWidth: CALENDAR_WRAPPER_MAX_WIDTH,
    padding: "S100",
    role: "dialog",
    "aria-labelledby": dialogHiddenTextId,
    "aria-modal": "true"
  }, allYears.map(function (year) {
    return ___EmotionJSX(YearButton, {
      hasFocus: year === focusedYear,
      isCurrentYear: year === boundedCurrentYear,
      key: year,
      onClick: handleYearClick,
      onKeyDown: handleKeyDown,
      year: year
    });
  }), ___EmotionJSX("span", {
    hidden: true,
    id: dialogHiddenTextId
  }, yearPickerButtonAltText)));
};

export default YearPicker;